/**
 * `nixDeployTarget` — stand a workspace up inside a `nix shell` and reach it over
 * `ws://127.0.0.1:<port>`. `tlsTermination: "none"` (loopback only).
 *
 * Generic on purpose: it resolves a flake stack attr and runs `skaile serve`
 * inside it, nothing more. Platform-specific deployment policy (store binds,
 * apparmor, fleet) stays in the host's own custom target — this built-in only
 * knows `flakeRef` + `stackAttr`. `nixBin` exists as a hermetic-test seam, the
 * same role `command` plays for the local target.
 */

import type { DeployTarget } from "@skaile/workspaces/plugin-registry";
import * as z from "zod";
import { allocateFreePort } from "./port.js";
import { buildProcessHandle, pidAlive, spawnProcess } from "./process-handle.js";
import { waitForLine } from "./stream-lines.js";

const nixConfigSchema = z
  .object({
    /** Flake reference holding the stack, e.g. `/etc/skaile/flake` or `github:org/repo`. */
    flakeRef: z.string(),
    /** Attr selected out of the flake, e.g. `stacks.default`. */
    stackAttr: z.string(),
    port: z.number().int().positive().optional(),
    env: z.record(z.string(), z.string()).optional(),
    cwd: z.string().optional(),
    readyPattern: z.string().optional(),
    /** Override the `nix` binary — for hermetic tests only. */
    nixBin: z.string().optional(),
  })
  .strict();

type NixConfig = z.infer<typeof nixConfigSchema>;

const nixPayloadSchema = z.object({
  pid: z.number(),
  port: z.number(),
});

type NixPayload = z.infer<typeof nixPayloadSchema>;

const DEFAULT_READY_PATTERN = "ws://";
const DEFAULT_READY_TIMEOUT_MS = 60_000;

/**
 * Build the `nix shell … --command skaile serve --port <port>` argv. Pure and
 * exported so the arg contract can be unit-tested without spawning nix.
 */
export function buildNixArgs(
  config: Pick<NixConfig, "flakeRef" | "stackAttr">,
  port: number,
): string[] {
  return [
    "shell",
    "--offline",
    `${config.flakeRef}#${config.stackAttr}`,
    "--command",
    "skaile",
    "serve",
    "--port",
    String(port),
  ];
}

/** Built-in target that runs the workspace inside a `nix shell`. */
export const nixDeployTarget: DeployTarget<NixConfig, NixPayload> = {
  id: "nix",
  displayName: "Nix shell",
  apiVersion: 1,
  tlsTermination: "none",
  configSchema: nixConfigSchema as unknown as z.ZodType<NixConfig>,
  payloadSchema: nixPayloadSchema,

  async create(config, ctx) {
    const log = ctx.log;
    const port = config.port ?? (await allocateFreePort());
    const cwd = config.cwd ?? process.cwd();
    const bin = config.nixBin ?? "nix";
    const args = buildNixArgs(config, port);
    const pattern = new RegExp(config.readyPattern ?? DEFAULT_READY_PATTERN);

    const spawned = spawnProcess(bin, args, {
      cwd,
      env: { ...process.env, ...ctx.env, ...config.env },
    });
    // portableSpawn returns pid -1 on spawn failure; a -1 pid would later turn
    // process.kill(pid) into a kill(-1) broadcast to every process we own.
    if (!spawned.proc.pid || spawned.proc.pid <= 0) {
      throw new Error("failed to spawn nix shell");
    }
    log.info("nix shell spawned", {
      pid: spawned.proc.pid,
      port,
      flakeRef: config.flakeRef,
      stackAttr: config.stackAttr,
    });

    const payload: NixPayload = { pid: spawned.proc.pid, port };
    const ready = (timeoutMs: number, signal: AbortSignal) =>
      waitForLine(spawned.buffer, pattern, { timeoutMs, signal });
    return buildProcessHandle(payload, ctx, ready, spawned, DEFAULT_READY_TIMEOUT_MS);
  },

  async restore(payload, ctx) {
    const log = ctx.log;
    if (!pidAlive(payload.pid)) {
      throw new Error(`nix deploy pid ${payload.pid} is no longer alive`);
    }
    log.info("re-attached to nix shell", { pid: payload.pid, port: payload.port });
    return buildProcessHandle(payload, ctx, async () => {}, null, DEFAULT_READY_TIMEOUT_MS);
  },
};
